import { useState, useRef, useCallback, useEffect } from "react";

interface UseDeepgramRecognitionOptions {
  apiKey: string;
  model?: string;
  language?: string;
  onError?: (error: string) => void;
}

interface UseDeepgramRecognitionReturn {
  transcript: string;
  interimTranscript: string;
  finalTranscript: string;
  listening: boolean;
  isSupported: boolean;
  error: string | null;
  startListening: (options?: { continuous?: boolean }) => Promise<void>;
  /** Drains in-flight audio, commits the last segment, then closes. Returns the full accumulated text. */
  stopListening: () => Promise<string>;
  resetTranscript: () => void;
}

function buildDeepgramUrl(model: string, language: string): string {
  const params = new URLSearchParams({
    model,
    language,
    smart_format: "true",
    punctuate: "true",
    numerals: "true",
    interim_results: "true",
    vad_events: "true",
    endpointing: "800",
    utterance_end_ms: "1500",
    diarize: "true",
    filler_words: "true",
    profanity_filter: "false",
  });
  return `wss://api.deepgram.com/v1/listen?${params.toString()}`;
}

export const useDeepgramRecognition = (
  options: UseDeepgramRecognitionOptions,
): UseDeepgramRecognitionReturn => {
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");
  const [listening, setListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const finalTranscriptRef = useRef("");

  useEffect(() => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setIsSupported(false);
    }
  }, []);

  const startListening = useCallback(
    async (listenOptions?: { continuous?: boolean }) => {
      setError(null);

      if (!options.apiKey) {
        const errorMsg = "Deepgram API key is required";
        setError(errorMsg);
        if (options.onError) options.onError(errorMsg);
        return;
      }

      if (listening) {
        return;
      }

      try {
        const streamPromise = navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("Microphone access timeout")),
            10000,
          ),
        );

        const stream = await Promise.race([streamPromise, timeoutPromise]);
        streamRef.current = stream;

        const url = buildDeepgramUrl(
          options.model || "nova-3-medical",
          options.language || "en-US",
        );

        // Browser WebSockets cannot send custom HTTP headers. Deepgram accepts
        // auth via the Sec-WebSocket-Protocol handshake header instead:
        //   Sec-WebSocket-Protocol: token, YOUR_API_KEY
        const ws = new WebSocket(url, ["token", options.apiKey]);
        wsRef.current = ws;

        await new Promise<void>((resolve, reject) => {
          ws.onopen = () => {
            resolve();
          };
          ws.onerror = () => {
            reject(new Error("WebSocket connection failed"));
          };
          ws.onclose = (event) => {
            reject(
              new Error(
                `WebSocket closed: ${event.code} ${event.reason || "no reason"}`,
              ),
            );
          };
        });

        setListening(true);
        setError(null);

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "Results") {
              const text = data.channel?.alternatives?.[0]?.transcript || "";

              if (text && text.trim().length > 0) {
                if (data.is_final) {
                  const updated = finalTranscriptRef.current
                    ? `${finalTranscriptRef.current} ${text}`
                    : text;
                  finalTranscriptRef.current = updated;
                  setFinalTranscript(updated);
                  setTranscript(updated);
                  setInterimTranscript("");
                } else {
                  setInterimTranscript(text);
                }
              }
            }
          } catch {
            // non-JSON frame, ignore
          }
        };

        ws.onerror = () => {
          let errorMsg = "Transcription service error. ";
          if (!navigator.onLine) {
            errorMsg = "Lost internet connection. Transcription stopped.";
          } else {
            errorMsg += "Please try again.";
          }
          setError(errorMsg);
          if (options.onError) options.onError(errorMsg);
          setListening(false);
        };

        ws.onclose = () => {
          setListening(false);
        };

        // Start capturing audio
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
          mediaRecorderRef.current = null;
        }

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: "audio/webm",
        });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (
            event.data.size > 0 &&
            wsRef.current?.readyState === WebSocket.OPEN
          ) {
            wsRef.current.send(event.data);
          }
        };

        mediaRecorder.start(250);
      } catch (error: any) {
        let errorMsg = "Failed to start transcription. ";

        if (error.name === "NotAllowedError") {
          errorMsg =
            "Microphone access denied. Please grant permission and try again.";
        } else if (error.name === "NotFoundError") {
          errorMsg =
            "No microphone found. Please connect a microphone and try again.";
        } else if (error.message === "Microphone access timeout") {
          errorMsg = "Microphone access timed out. Please try again.";
        } else if (!navigator.onLine) {
          errorMsg =
            "No internet connection. Please check your network and try again.";
        } else {
          errorMsg += error.message || "Please try again.";
        }

        setError(errorMsg);
        if (options.onError) options.onError(errorMsg);
        setListening(false);
      }
    },
    [options],
  );

  const stopListening = useCallback(async (): Promise<string> => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // already stopped
      }
      mediaRecorderRef.current = null;
    }

    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "Finalize" }));
          await new Promise<void>((r) => setTimeout(r, 1200));
        }
      } catch {
        // connection already closing
      }

      try {
        wsRef.current.close(1000);
      } catch {
        // already closed
      }
      wsRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setListening(false);
    setError(null);

    return finalTranscriptRef.current;
  }, []);

  const resetTranscript = useCallback(() => {
    finalTranscriptRef.current = "";
    setTranscript("");
    setInterimTranscript("");
    setFinalTranscript("");
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  return {
    transcript: transcript + (interimTranscript ? " " + interimTranscript : ""),
    interimTranscript,
    finalTranscript,
    listening,
    isSupported,
    error,
    startListening,
    stopListening,
    resetTranscript,
  };
};
